import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Clock } from "lucide-react";

const EMPTY = {
  title: "", description: "", image_url: "", meal_type: "dinner", tags: "",
  time_minutes: "", servings: 1, calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0,
  ingredients: "", steps: "",
};

export default function CoachRecipes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/recipes").then((r) => setItems(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const startEdit = (r) => {
    setEditId(r.recipe_id);
    setForm({
      title: r.title, description: r.description || "", image_url: r.image_url || "",
      meal_type: r.meal_type, tags: (r.tags || []).join(", "), time_minutes: r.time_minutes || "",
      servings: r.servings || 1, calories: r.calories, protein: r.protein, carbs: r.carbs,
      fats: r.fats, fiber: r.fiber, ingredients: (r.ingredients || []).join("\n"), steps: (r.steps || []).join("\n"),
    });
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title, description: form.description, image_url: form.image_url,
      meal_type: form.meal_type,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      time_minutes: form.time_minutes ? parseInt(form.time_minutes, 10) : null,
      servings: parseInt(form.servings, 10) || 1,
      calories: parseFloat(form.calories) || 0, protein: parseFloat(form.protein) || 0,
      carbs: parseFloat(form.carbs) || 0, fats: parseFloat(form.fats) || 0, fiber: parseFloat(form.fiber) || 0,
      ingredients: form.ingredients.split("\n").map((s) => s.trim()).filter(Boolean),
      steps: form.steps.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editId) await api.patch(`/recipes/${editId}`, payload);
      else await api.post("/recipes", payload);
      toast.success(editId ? "Recipe updated" : "Recipe created");
      setForm(EMPTY); setEditId(null); setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this recipe?")) return;
    await api.delete(`/recipes/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-10" data-testid="coach-recipes">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Nourish</div>
          <h1 className="font-serif text-5xl mt-3">Recipes</h1>
          <p className="text-secondary-luxe text-sm mt-3">The recipe library your clients and members browse and log from.</p>
        </div>
        <button className="btn-primary" onClick={() => { setOpen(!open); setEditId(null); setForm(EMPTY); }} data-testid="new-recipe-btn">
          <Plus size={14} /> New recipe
        </button>
      </header>

      {open && (
        <form onSubmit={submit} className="card-luxe space-y-5 max-w-3xl" data-testid="recipe-form">
          <div className="overline">{editId ? "Edit recipe" : "New recipe"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-luxe">Title</label>
              <input className="input-luxe" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="recipe-title" />
            </div>
            <div>
              <label className="label-luxe">Photo URL</label>
              <input className="input-luxe" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" data-testid="recipe-image-url" />
            </div>
          </div>
          <div>
            <label className="label-luxe">Description</label>
            <textarea className="input-luxe" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="recipe-description" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <label className="label-luxe">Meal</label>
              <select className="input-luxe" value={form.meal_type} onChange={(e) => setForm({ ...form, meal_type: e.target.value })} data-testid="recipe-meal-type">
                {["breakfast", "lunch", "dinner", "snack"].map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="label-luxe">Time (min)</label>
              <input type="number" className="input-luxe" value={form.time_minutes} onChange={(e) => setForm({ ...form, time_minutes: e.target.value })} />
            </div>
            <div>
              <label className="label-luxe">Servings</label>
              <input type="number" min="1" className="input-luxe" value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} />
            </div>
            <div>
              <label className="label-luxe">Tags (comma-sep)</label>
              <input className="input-luxe" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="High-Protein, Sweet" />
            </div>
          </div>
          <div className="grid grid-cols-5 gap-4">
            {[["calories", "Calories"], ["protein", "Protein g"], ["carbs", "Carbs g"], ["fats", "Fat g"], ["fiber", "Fiber g"]].map(([k, l]) => (
              <div key={k}>
                <label className="label-luxe">{l}</label>
                <input type="number" step="any" className="input-luxe" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`recipe-${k}`} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-luxe">Ingredients (one per line)</label>
              <textarea className="input-luxe" rows={6} value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} data-testid="recipe-ingredients" />
            </div>
            <div>
              <label className="label-luxe">Steps (one per line)</label>
              <textarea className="input-luxe" rows={6} value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })} data-testid="recipe-steps" />
            </div>
          </div>
          <button className="btn-primary" data-testid="recipe-submit">{editId ? "Save changes" : "Create recipe"}</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((r) => (
          <div key={r.recipe_id} className="card-luxe p-0 overflow-hidden" data-testid={`coach-recipe-${r.recipe_id}`}>
            {r.image_url && <img src={r.image_url} alt={r.title} className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="flex items-center justify-between">
                <span className="overline">{r.meal_type}</span>
                {r.time_minutes && <span className="text-xs text-tertiary-luxe flex items-center gap-1"><Clock size={11} /> {r.time_minutes} min</span>}
              </div>
              <h3 className="font-serif text-xl mt-2">{r.title}</h3>
              <div className="text-xs text-secondary-luxe mt-2">
                {Math.round(r.calories)} cal · {Math.round(r.protein)}P / {Math.round(r.carbs)}C / {Math.round(r.fats)}F
              </div>
              <div className="mt-4 flex gap-2">
                <button className="btn-secondary text-xs" onClick={() => startEdit(r)} data-testid={`edit-recipe-${r.recipe_id}`}><Pencil size={12} /> Edit</button>
                <button className="btn-ghost" onClick={() => remove(r.recipe_id)} data-testid={`delete-recipe-${r.recipe_id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-secondary-luxe">No recipes yet.</div>}
      </div>
    </div>
  );
}
